import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import api from "../lib/api";

// Address input with suggestions from Google Places + your previously-saved
// addresses (via the backend /places/autocomplete proxy). API-compatible with the
// old component: value, onChange, onSelect. `containerClassName` styles the wrapper
// (e.g. grid col-span); `className` styles the input.
export function AddressAutocomplete({ value, onChange, onSelect, className, containerClassName = "", placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const [sugs, setSugs] = useState([]);
  const [hi, setHi] = useState(-1);
  const boxRef = useRef(null);
  const tRef = useRef(null);
  const skipRef = useRef(false);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  function query(v) {
    if (tRef.current) clearTimeout(tRef.current);
    if (!v || v.trim().length < 2) { setSugs([]); setOpen(false); return; }
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/places/autocomplete", { params: { q: v } });
        const list = data.suggestions || [];
        setSugs(list); setOpen(list.length > 0); setHi(-1);
      } catch { setSugs([]); setOpen(false); }
    }, 300);
  }

  function handleChange(e) {
    const v = e.target.value;
    onChange(v);
    if (skipRef.current) { skipRef.current = false; return; }
    query(v);
  }

  function pick(s) {
    skipRef.current = true;
    onChange(s.description);
    if (onSelect) onSelect(s.description);
    setOpen(false); setSugs([]);
  }

  function onKeyDown(e) {
    if (!open || sugs.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, sugs.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(sugs[hi]); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  return (
    <div className={`relative ${containerClassName}`} ref={boxRef}>
      <input className={className} placeholder={placeholder} value={value} autoComplete="off"
        onChange={handleChange} onFocus={() => { if (sugs.length) setOpen(true); }}
        onKeyDown={onKeyDown} data-testid={testId} />
      {open && sugs.length > 0 && (
        <ul className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E5E3DB] shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestions">
          {sugs.map((s, i) => (
            <li key={s.place_id || s.description || i} onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              className={`px-3 py-2 text-sm cursor-pointer flex items-start gap-2 hover:bg-[#F5F4F0] ${i === hi ? "bg-[#F5F4F0]" : ""}`}
              data-testid={`address-suggestion-${i}`}>
              <MapPin className="w-3.5 h-3.5 text-[#C84B31] mt-0.5 shrink-0" />
              <span className="flex-1 text-[#1A1A18]">{s.description}</span>
              {s.source === "saved" && <span className="text-[9px] uppercase text-[#68665E] bg-[#EDEBE3] px-1 rounded self-center">saved</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
